import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import {
  ContractVersionsPageDocument,
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesJobCatalogContextlessPoliciesFragment,
  ContractsEmployeesReferenceContractFragment,
  EmployeeProfilePageDocument,
  JobCatalogTreeFragment,
  JobPositionLevelDocument,
  JobPositionRoleDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import type { FlashType } from 'modules/core/store/models/app'
import app from 'modules/core/store/models/app'

import DatesAndDetails from 'modules/contracts/components/FlowPage/Steps/DatesAndDetails'
import RoleLevelSalary from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary'
import { FlowPageContent } from 'modules/contracts/components/FlowPage/_FlowPageContent'
import ContractFlow from 'modules/contracts/lib/ContractFlow'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'

import Footer from './_Footer'
import DatesDetailsStep from './lib/DatesDetailsStep'
import RoleLevelSalaryStep from './lib/RoleLevelSalaryStep'
import { translations as datesStepTranslations } from './lib/datesDetailsTranslations'
import { getRequestPromoteFlowForm } from './lib/requestPromoteFlowForm'
import { translations as roleLevelSalaryTranslations } from './lib/roleLevelSalaryTranslations'
import { translations as templateTranslations } from './lib/templateTranslations'

interface Props {
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
  contractVersion: ContractsEmployeesReferenceContractFragment
  contractVersions: ContractsEmployeesContractVersionFragment[]
  jobCatalogContextlessPolicies: ContractsEmployeesJobCatalogContextlessPoliciesFragment | undefined
  hasPayrollBundle: boolean
}

const RequestPromoteFlowContent = ({
  jobCatalogTrees,
  jobCatalogTreeFeatureEnabled,
  contractVersion,
  contractVersions,
  jobCatalogContextlessPolicies,
  hasPayrollBundle,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const apolloClient = useApolloClient()
  const { compensations, legalEntityContractData } = useContractFlow()
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const form = useLazyRef(() => getRequestPromoteFlowForm(contractVersion)).current

  const onClose = () => {
    navigate(
      navigation.employees.id.contractVersions.fullPath({
        id: contractVersion.employee?.id.toString() ?? '',
      })
    )
  }
  const [isCurrentStepCompleted, setIsCurrentStepCompleted] = React.useState(false)

  const requestPromoteContractFlow = useLazyRef(
    () =>
      new ContractFlow([
        new DatesDetailsStep(
          <DatesAndDetails
            step={1}
            type="request_promote"
            form={form}
            translations={datesStepTranslations()}
            setIsCurrentStepCompleted={setIsCurrentStepCompleted}
            contractVersions={contractVersions}
          />
        ),
        new RoleLevelSalaryStep(
          <RoleLevelSalary
            step={2}
            type="request_promote"
            form={form}
            translations={roleLevelSalaryTranslations()}
            setIsCurrentStepCompleted={setIsCurrentStepCompleted}
            jobCatalogTrees={jobCatalogTrees}
            jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
            jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
            canSeeJobTitle={contractVersion._policies?.seeJobTitle.can ?? false}
            contractVersion={contractVersion}
            employee={contractVersion.employee}
            canSeeGrossSalary={contractVersion?._policies?.seeGrossSalary.can ?? false}
            canSeeCompensations={contractVersion?._policies?.seeAdditionalCompensation.can ?? false}
            canSeePayrollPolicy={contractVersion?._policies?.seePayrollPolicy.can ?? false}
            canEditPayrollPolicy={contractVersion?._policies?.seePayrollPolicy.can ?? false}
            legalEntityId={contractVersion.employee?.legalEntity?.id ?? 0}
            hasPayrollBundle={hasPayrollBundle}
          />
        ),
      ])
  ).current

  const showFlashMessage = (type: FlashType, message: string) => {
    app.setFlash({
      type,
      message,
    })
  }

  const onSubmit = async () => {
    setIsSubmitting(true)
    const api = ApiClient()
    const data = form.serialized
    data.additional_compensations =
      compensations?.map((compensation) => compensation.attributes) ?? []
    data.employee_id = contractVersion.employee?.id

    try {
      await api.post(`/api/rest/contracts/contract_version_requests/create_promote_request`, data)

      showFlashMessage(
        'correct',
        i18n.t('contract_versions.flows.request_promote.confirmation', {
          employeeFullName: contractVersion.employee?.fullName ?? '',
        })
      )

      await apolloClient.refetchQueries({
        include: [
          EmployeeProfilePageDocument,
          ContractVersionsPageDocument,
          JobPositionLevelDocument,
          JobPositionRoleDocument,
        ],
      })

      navigate(
        navigation.employees.id.contractVersions.fullPath({
          id: contractVersion.employee?.id.toString() ?? '',
        })
      )
    } catch {
      showFlashMessage('wrong', i18n.t('contract_versions.flows.errors.unknown'))
      setIsSubmitting(false)
    }
  }

  return (
    <FlowPageContent
      translations={templateTranslations()}
      form={form}
      contractVersion={contractVersion}
      legalEntityContractData={legalEntityContractData}
      title={i18n.t('contract_versions.flows.request_promote.header.title', {
        employeeFullName: contractVersion.employee?.fullName ?? '',
      })}
      flow={requestPromoteContractFlow}
      footerContent={
        <Footer
          flow={requestPromoteContractFlow}
          employeeFullName={contractVersion.employee?.fullName ?? ''}
        />
      }
      isCurrentStepCompleted={isCurrentStepCompleted}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={() => onSubmit()}
      employee={contractVersion.employee}
    />
  )
}

export default observer(RequestPromoteFlowContent)
